import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  Container,
  Row,
  Col,
  Input,
  Label,
  FormGroup,
  Pagination,
  PaginationItem,
  PaginationLink,
  Badge,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  Table,
  FormFeedback,
} from "reactstrap";
import { useNavigate } from "react-router-dom";

import {
  GetAllRooms,
  AddRoom,
  UpdateRoom,
  getRoomHistory,
  getRoomHistoryByDateRange,
} from "../../helpers/api.ts";
import { toast, ToastContainer } from "react-toastify";
import Breadcrumbs from "../../components/Common/Breadcrumb.js";

const RoomManagement = () => {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [modal, setModal] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [filters, setFilters] = useState({
    room_type: "",
    roomstatus: "",
    pageSize: 10,
  });

  const [formData, setFormData] = useState({
    room_number: "",
    room_type: "SINGLE",
    ac_no_ac: "AC",
    roomstatus: "AVAILABEL",
    tariff: "",
    description: "",
    room_name: "",
  });

  // State for duplicate validation error
  const [roomNumberError, setRoomNumberError] = useState("");

  const navigate = useNavigate();

  const roomTypes = ["SINGLE", "DOUBLE", "SUITE", "DORMETORY", "BANQUET HALL"];
  // Note: Keeping "AVAILABEL" (misspelled) as backend expects this value
  // Display is handled by statusDisplayMap to show "AVAILABLE" to users
  const roomStatuses = ["AVAILABEL", "OCCUPIED", "UNDER MAINTAINANCE"];

  // Helper function to display correct spelling in UI
  const getDisplayStatus = (status) => {
    if (status === "AVAILABEL") return "AVAILABLE";
    return status;
  };

  // Mapping for dropdown display
  const statusDisplayMap = {
    "AVAILABEL": "AVAILABLE",
    "OCCUPIED": "OCCUPIED",
    "UNDER MAINTAINANCE": "UNDER MAINTAINANCE"
  };

  useEffect(() => {
    fetchRooms();
  }, [currentPage, filters]);

  const fetchRooms = async () => {
    try {
      setLoading(true);
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      let hotel_id1;
      if (user?.role === "RECEPTIONIST") {
        hotel_id1 = user?.hotel?.hotel_id;
      } else {
        hotel_id1 = user?.hotel_id;
      }

      const userData = {
        page: currentPage,
        pageSize: filters.pageSize,
        room_type: filters.room_type,
        roomstatus: filters.roomstatus,
        hotel_id: hotel_id1,

        // hotel_id: user?.hotel_id,
        // hotel_id: user?.hotel?.hotel_id || ''
      };

      const response = await GetAllRooms(userData);

      if (response.success) {
        setRooms(response.data || []);
        setTotalPages(response.totalPages || 1);
      } else {
        setRooms([]);
        setTotalPages(1);
      }
    } catch (error) {
      console.error("Error fetching rooms:", error);
      setRooms([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear room number error when user types
    if (name === "room_number") {
      setRoomNumberError("");
    }
  };

  // Check for duplicate room number
  const checkDuplicateRoomNumber = (roomNumber) => {
    const trimmedRoomNumber = roomNumber.trim().toUpperCase();
    
    // When editing, exclude the current room from duplicate check
    const duplicateRoom = rooms.find((room) => {
      const isSameRoom = editingRoom && room.room_id === editingRoom.room_id;
      const isDuplicate = room.room_number.trim().toUpperCase() === trimmedRoomNumber;
      return !isSameRoom && isDuplicate;
    });

    return duplicateRoom;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check for duplicate room number before submitting
    const duplicateRoom = checkDuplicateRoomNumber(formData.room_number);
    
    if (duplicateRoom) {
      setRoomNumberError(`Room number "${formData.room_number}" already exists. Please use a different room number.`);
      toast.error(`Room number "${formData.room_number}" already exists!`);
      return;
    }

    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");

      // Include user ID and hotel ID in the form data
      const idByRole =
        user.role === "HOTEL_ADMIN" ? user?.hotel_id : user?.hotel?.hotel_id;
      const parsedTariff =
        formData.tariff === undefined || formData.tariff === null || formData.tariff === ""
          ? ""
          : parseFloat(formData.tariff);

      const formDataToSend = {
        ...formData,
        hotel_id: idByRole,
        createdBy: user?.id, // Add user ID for creation
        updatedBy: user?.id, // Add user ID for updates
        tariff: parsedTariff,
      };

      let response;
      if (editingRoom) {
        console.log("Updating room payload:", formDataToSend);
        response = await UpdateRoom(editingRoom.room_id, formDataToSend);
      } else {
        response = await AddRoom(formDataToSend);
      }

      if (response.success) {
        toast.success(
          editingRoom
            ? "Room updated successfully!"
            : "Room added successfully!"
        );
        setModal(false);
        fetchRooms();
        resetForm();
      } else {
        toast.error(response.message || "Operation failed");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error(
        error.message === "Request failed with status code 403"
          ? "Cannot create more rooms - hotel room limit reached. Contact hotel owner to increase limit"
          : "Operation failed"
      );
    }
  };

  const resetForm = () => {
    setFormData({
      room_type: "SINGLE",
      ac_no_ac: "AC",
      roomstatus: "AVAILABEL",
      tariff: "",
      description: "",
      room_number: "",
      room_name: "",
    });
    setEditingRoom(null);
    setRoomNumberError(""); // Clear error on reset
  };

  const handleEdit = (room) => {
    setEditingRoom(room);
    setFormData({
      room_number: room.room_number,
      room_type: room.room_type,
      roomstatus: room.roomstatus,
      room_name: room.room_name,
      ac_no_ac: room.ac_no_ac,
      tariff: room.tariff?.toString?.() || room.tariff || "",
      description: room.description || "",
    });
    setRoomNumberError(""); // Clear error when opening edit modal
    setModal(true);
  };

  const getStatusBadgeColor = (status) => {
    switch (status.toUpperCase()) {
      case "AVAILABEL":
        return "success";
      case "OCCUPIED":
        return "warning";
      case "UNDER MAINTAINANCE":
        return "danger";
      default:
        return "secondary";
    }
  };

  const handleViewHistory = (room) => {
    navigate(`/room-history/${room.room_id}/${room.room_number}`);
  };

  const renderRoomTable = () => (
    <div className="table-responsive">
      <table className="table table-centered table-nowrap mb-0">
        <thead>
          <tr>
            <th>Sr. No</th>
            <th>Room Number</th>
            <th>Room Type</th>
            <th>Room Name</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={5} className="text-center">
                Loading...
              </td>
            </tr>
          ) : rooms.length === 0 ? (
            <tr>
              <td colSpan={5} className="text-center">
                No rooms found
              </td>
            </tr>
          ) : (
            rooms.map((room, i) => (
              <tr key={room.room_id}>
                <td>{(currentPage - 1) * filters.pageSize + i + 1}</td>

                <td>
                  <span
                    style={{
                      color: "#007bff",
                      // textDecoration: "underline",
                      cursor: "pointer",
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      handleViewHistory(room);
                    }}
                  >
                    {room.room_number}
                  </span>
                </td>
                <td>{room.room_type}</td>
                <td>{room.room_name}</td>
                <td>
                  <Badge color={getStatusBadgeColor(room.roomstatus)}>
                    {getDisplayStatus(room.roomstatus)}
                  </Badge>
                </td>
                <td>
                  <Button
                    color="info"
                    size="sm"
                    className="me-2"
                    onClick={() => handleEdit(room)}
                  >
                    Edit
                  </Button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="page-content">
      <ToastContainer />
      <Container fluid>
        <Breadcrumbs title="Rooms" breadcrumbItem="Manage Rooms" />
        <Card>
          <CardBody>
            <div className="d-flex justify-content-between align-items-center flex-column flex-md-row mb-4 gap-2">
              <h4 className="card-title mb-0">Room Management</h4>
              <div className="d-flex gap-2 w-100 w-md-auto">
                <Button
                  color="secondary"
                  onClick={() => navigate("/dashboard")}
                  className="w-100 w-md-auto"
                >
                  Back
                </Button>
                <Button
                  color="primary"
                  onClick={() => {
                    resetForm();
                    setModal(true);
                  }}
                  className="w-100 w-md-auto"
                >
                  Add New Room
                </Button>
              </div>
            </div>

            <Row className="mb-4 g-2">
              <Col xs={12} md={6}>
                <FormGroup>
                  <Label for="roomType">Room Type</Label>
                  <Input
                    type="select"
                    id="roomType"
                    value={filters.room_type}
                    onChange={(e) =>
                      handleFilterChange("room_type", e.target.value)
                    }
                  >
                    <option value="">All Types</option>
                    {roomTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col xs={12} md={6}>
                <FormGroup>
                  <Label for="roomStatus">Room Status</Label>
                  <Input
                    type="select"
                    id="roomStatus"
                    value={filters.roomstatus}
                    onChange={(e) =>
                      handleFilterChange("roomstatus", e.target.value)
                    }
                  >
                    <option value="">All Statuses</option>
                    {roomStatuses.map((status) => (
                      <option key={status} value={status}>
                        {statusDisplayMap[status] || status}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
            </Row>

            {renderRoomTable()}

            {totalPages > 1 && (
              <Pagination className="pagination pagination-rounded justify-content-end mt-3">
                <PaginationItem disabled={currentPage === 1}>
                  <PaginationLink
                    previous
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setCurrentPage((prev) => Math.max(1, prev - 1));
                    }}
                  />
                </PaginationItem>

                {[...Array(totalPages)].map((_, i) => (
                  <PaginationItem key={i + 1} active={currentPage === i + 1}>
                    <PaginationLink
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setCurrentPage(i + 1);
                      }}
                    >
                      {i + 1}
                    </PaginationLink>
                  </PaginationItem>
                ))}

                <PaginationItem disabled={currentPage === totalPages}>
                  <PaginationLink
                    next
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setCurrentPage((prev) => Math.min(totalPages, prev + 1));
                    }}
                  />
                </PaginationItem>
              </Pagination>
            )}
          </CardBody>
        </Card>

        <Modal isOpen={modal} toggle={() => setModal(!modal)} className="modal-fullscreen-md-down">
          <Form onSubmit={handleSubmit}>
            <ModalHeader toggle={() => setModal(!modal)}>
              {editingRoom ? "Edit Room" : "Add New Room"}
            </ModalHeader>
            <ModalBody>
              <Row className="g-2">
                <Col xs={12} md={6}>
                  <FormGroup>
                    <Label for="room_name">Room Name</Label>
                    <Input
                      type="text"
                      name="room_name"
                      id="room_name"
                      value={formData.room_name}
                      onChange={handleInputChange}
                      required
                    />
                  </FormGroup>
                </Col>
                <Col xs={12} md={6}>
                  <FormGroup>
                    <Label for="room_number">Room Number</Label>
                    <Input
                      type="text"
                      name="room_number"
                      id="room_number"
                      value={formData.room_number}
                      onChange={handleInputChange}
                      invalid={!!roomNumberError}
                      required
                    />
                    {roomNumberError && (
                      <FormFeedback>{roomNumberError}</FormFeedback>
                    )}
                  </FormGroup>
                </Col>
              </Row>
              <Row className="g-2">
                <Col xs={12} md={6}>
                  <FormGroup>
                    <Label for="room_type">Room Type</Label>
                    <Input
                      type="select"
                      name="room_type"
                      id="room_type"
                      value={formData.room_type}
                      onChange={handleInputChange}
                      required
                    >
                      {roomTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col xs={12} md={6}>
                  <FormGroup>
                    <Label for="ac_no_ac">AC/Non-AC</Label>
                    <Input
                      type="select"
                      name="ac_no_ac"
                      id="ac_no_ac"
                      value={formData.ac_no_ac}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="AC">AC</option>
                      <option value="NON-AC">NON-AC</option>
                    </Input>
                  </FormGroup>
                </Col>
              </Row>

              <Row className="g-2">
                <Col xs={12} md={6}>
                  <FormGroup>
                    <Label for="roomstatus">Status</Label>
                    <Input
                      type="select"
                      name="roomstatus"
                      id="roomstatus"
                      value={formData.roomstatus}
                      onChange={handleInputChange}
                      required
                    >
                      {roomStatuses.map((status) => (
                        <option key={status} value={status}>
                          {statusDisplayMap[status] || status}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col xs={12} md={6}>
                  <FormGroup>
                    <Label for="tariff">Room Tariff</Label>
                    <Input
                      type="number"
                      name="tariff"
                      id="tariff"
                      value={formData.tariff}
                      onChange={handleInputChange}
                      required
                    />
                  </FormGroup>
                </Col>
              </Row>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={() => setModal(false)}>
                Cancel
              </Button>
              <Button color="primary" type="submit">
                {editingRoom ? "Update" : "Save"}
              </Button>
            </ModalFooter>
          </Form>
        </Modal>
      </Container>
      <ToastContainer />
    </div>
  );
};

export default RoomManagement;